import type { IOmit } from '@sixui/ui';
import {
  Button,
  Dialog,
  IDialogProps,
  Labeled,
  TextInputField,
  useId,
} from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { Link } from '~/components/atoms/Link';
import { ContentBlock } from '~/components/molecules/ContentBlock';

export type IIframeMdxDirectiveDialogFormValues = {
  url?: string;
};

export type IIframeMdxDirectiveDialogProps = IOmit<
  IDialogProps,
  'headline' | 'actions' | 'children'
> & {
  onSubmit?: (
    values: IIframeMdxDirectiveDialogFormValues,
    event?: React.BaseSyntheticEvent,
  ) => void;
  defaultValues?: IIframeMdxDirectiveDialogFormValues;
};

const styles = stylex.create({
  host: {
    minWidth: 480,
  },
});

export const IframeMdxDirectiveDialogForm: React.FC<
  IIframeMdxDirectiveDialogProps
> = (props) => {
  const { sx, onSubmit, defaultValues, ...other } = props;
  const { t } = useTranslation();
  const form = useForm<IIframeMdxDirectiveDialogFormValues>({
    defaultValues,
  });
  const formId = useId();

  return (
    <Dialog
      {...other}
      sx={[styles.host, sx]}
      headline={t('IframeMdxDirectiveDialogForm.title')}
      actions={({ close }) => (
        <>
          <Button
            variant="text"
            onClick={close}
            disabled={form.formState.isSubmitting}
            form={formId}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onSubmit ? form.handleSubmit(onSubmit) : undefined}
            form={formId}
          >
            {t('common.insert')}
          </Button>
        </>
      )}
    >
      <ContentBlock.FormBody
        id={formId}
        form={form}
        onSubmit={onSubmit}
        hideActionBar={true}
        extended
      >
        <FormSection>
          <Trans parent="p" i18nKey="IframeMdxDirectiveDialogForm.warning.p1" />
          <Trans
            parent="p"
            i18nKey="IframeMdxDirectiveDialogForm.warning.p2"
            components={{
              a: (
                <Link
                  to="https://iframetester.com/"
                  target="_blank"
                  tabIndex={-1}
                />
              ),
            }}
          />
          <Labeled
            label={t('IframeMdxDirectiveDialogForm.field.url.label')}
            required
          >
            <FormField
              as={TextInputField}
              forwardError
              control={form.control}
              name="url"
              type="url"
            />
          </Labeled>
        </FormSection>
      </ContentBlock.FormBody>
    </Dialog>
  );
};
